import * as RadixSelect from '@radix-ui/react-select'
import { Check, ChevronDown, X } from 'lucide-react'
import { INVALID_CONTROL } from './Field'
import { cn } from '@/lib/utils'

export interface SelectOption {
  value: string
  label: string
  /** Colour chip shown before the label, used by dye-source pickers. */
  swatch?: string | null
  description?: string
}

export interface SelectProps {
  value?: string | null
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  id?: string
  className?: string
  /** Shows a clear button while a value is chosen; Radix has no empty option of its own. */
  onClear?: () => void
}

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select',
  disabled,
  invalid,
  id,
  className,
  onClear,
}: SelectProps) {
  const clearable = Boolean(onClear) && value !== null && value !== undefined && value !== ''

  return (
    // Radix keeps showing the last item when `value` becomes undefined, so an
    // empty string is what returns the trigger to its placeholder.
    <RadixSelect.Root value={value ?? ''} onValueChange={onChange} disabled={disabled}>
      <div className="relative">
        <RadixSelect.Trigger
          id={id}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-[var(--radius-control)] border border-line',
            'bg-paper px-3 text-sm text-ink transition-colors data-[placeholder]:text-ink-faint',
            'focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:bg-paper-muted',
            invalid && INVALID_CONTROL,
            clearable && 'pr-14',
            className,
          )}
        >
          <RadixSelect.Value placeholder={placeholder} />
          <RadixSelect.Icon>
            <ChevronDown className="size-4 text-ink-faint" />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        {clearable && !disabled && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear selection"
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-ink-faint hover:bg-paper-muted hover:text-ink"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-72 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-[var(--radius-control)] border border-line bg-paper shadow-[var(--shadow-float)]"
        >
          <RadixSelect.Viewport className="p-1">
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-ink outline-none data-[highlighted]:bg-brand-soft data-[state=checked]:text-brand"
              >
                {option.swatch && (
                  <span
                    className="size-3 shrink-0 rounded-full border border-line"
                    style={{ backgroundColor: option.swatch }}
                  />
                )}
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                {option.description && (
                  <span className="ml-auto truncate text-xs italic text-ink-faint">
                    {option.description}
                  </span>
                )}
                <RadixSelect.ItemIndicator className={cn(option.description ? 'ml-2' : 'ml-auto')}>
                  <Check className="size-4" />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
            {options.length === 0 && (
              <p className="px-2 py-3 text-sm text-ink-faint">Nothing to choose from yet.</p>
            )}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  )
}

/** Convenience for the many selects backed by a fixed string union. */
export function toOptions(values: readonly string[]): SelectOption[] {
  return values.map((value) => ({ value, label: value }))
}
